import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDate } from "@/lib/format";
import { StatusChip } from "@/components/common/StatusChip";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { prioridadTone } from "@/lib/wo";

export const ProposalPanel = ({ open, onClose, fecha, brand, onConfirmed }) => {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["schedule-propuesta", fecha, brand],
    enabled: open,
    queryFn: async () => (await api.get("/work-orders/schedule/propuesta", { params: { fecha, brand } })).data,
  });

  const { data: subsData } = useQuery({
    queryKey: ["subs-active-select"],
    enabled: open,
    queryFn: async () => (await api.get("/subs", { params: { status: "active", page_size: 200 } })).data,
  });

  useEffect(() => {
    if (data?.propuesta) setRows(data.propuesta);
  }, [data]);

  if (!open) return null;

  const setSub = (workOrderId, subId) => {
    const sub = (subsData?.items || []).find((s) => s.id === subId);
    setRows((rs) => rs.map((r) => (r.work_order_id === workOrderId
      ? { ...r, sub_id: subId, sub_nombre: sub?.nombre, ya_asignado: false } : r)));
  };

  const confirm = async () => {
    setConfirming(true);
    const programadas = [];
    const sinCosto = [];
    try {
      for (const row of rows) {
        if (row.sub_id && !row.ya_asignado) {
          const { data: detail } = await api.get(`/work-orders/${row.work_order_id}`);
          const wo = detail.wo;
          const cost = wo.sub_cost_cents > 0 ? wo.sub_cost_cents : (wo.tarifa_costo_sub_cents || 0);
          if (cost > 0) {
            await api.post(`/work-orders/${row.work_order_id}/assign`, {
              sub_id: row.sub_id, sub_cost_cents: cost, asignar: true, notificar: true,
              motivo: wo.sub_id ? t("schedule.reassign_reason") : undefined,
            });
            programadas.push(row.work_order_id);
          } else {
            sinCosto.push(row);
          }
        } else {
          programadas.push(row.work_order_id);
        }
      }
      if (programadas.length) {
        await api.post("/work-orders/schedule/confirmar", {
          fecha, items: programadas.map((id) => ({ work_order_id: id })),
        });
        toast.success(t("schedule.proposal.confirmed", { n: programadas.length }));
      }
      if (sinCosto.length) toast.error(`${t("schedule.no_cost")} (${sinCosto.length})`);
      onConfirmed?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ backgroundColor: "var(--ink-overlay)" }} data-testid="schedule-proposal-panel">
      <div className="flex h-full w-full max-w-[520px] flex-col border-l border-line bg-paper">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold text-ink">
              {t("schedule.proposal.title", { fecha: formatDate(fecha) })}
            </h2>
            <p className="mt-0.5 text-[12px] text-ink-tertiary">{t("schedule.proposal.desc")}</p>
          </div>
          <button data-testid="schedule-proposal-close" onClick={onClose}
            className="flex h-8 w-8 items-center justify-center text-ink-tertiary hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="py-8 text-center text-[12.5px] text-ink-tertiary">
              <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" /> {t("schedule.proposal.loading")}
            </p>
          ) : isError ? (
            <p data-testid="schedule-proposal-error" className="py-8 text-center text-[12.5px] text-state-critical">
              {t("app.error")}
            </p>
          ) : !rows.length && !data?.sin_candidato?.length ? (
            <p className="py-8 text-center text-[12.5px] text-ink-tertiary">{t("schedule.proposal.empty")}</p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.work_order_id} data-testid={`proposal-row-${r.work_order_id}`}
                  className="border border-line px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="min-w-0 truncate text-[12.5px] font-medium text-ink">
                      <span className="font-mono text-ink-tertiary">{r.code}</span> · {r.property_nombre}
                      {r.unit_etiqueta ? ` · ${r.unit_etiqueta}` : ""}
                    </p>
                    <StatusChip tone={prioridadTone(r.prioridad)} label={t(`priority.${r.prioridad}`)} />
                  </div>
                  <p className="mt-1.5 text-[11px] uppercase tracking-[0.05em] text-ink-tertiary">
                    {t("schedule.proposal.candidate")}
                  </p>
                  <Select value={r.sub_id || ""} onValueChange={(v) => setSub(r.work_order_id, v)}>
                    <SelectTrigger data-testid={`proposal-sub-select-${r.work_order_id}`} className="mt-1 h-8 text-[12.5px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(subsData?.items || []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              {!!data?.sin_candidato?.length && (
                <div className="pt-3">
                  <p className="mb-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.06em] text-state-critical">
                    <AlertTriangle className="h-3.5 w-3.5" /> {t("schedule.proposal.no_candidate_title")}
                  </p>
                  {data.sin_candidato.map((w) => (
                    <div key={w.id} data-testid={`proposal-no-candidate-${w.id}`}
                      className="mb-1.5 border-l-2 border-state-critical bg-state-critical-soft px-3 py-2">
                      <p className="text-[12.5px] font-medium text-ink">
                        <span className="font-mono text-ink-tertiary">{w.code}</span> · {w.property_nombre}
                        {w.unit_etiqueta ? ` · ${w.unit_etiqueta}` : ""}
                      </p>
                      <p className="mt-0.5 text-[11.5px] text-ink-secondary">{w.motivo}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">{t("app.cancel")}</Button>
          <Button data-testid="schedule-proposal-confirm" disabled={!rows.length || confirming} onClick={confirm}
            className="h-9 text-[13px]">
            {confirming && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("schedule.proposal.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
};
